
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// module components
import ExpressOrdersFilters from '@/modules/OrdersHistoryModule/components/ExpressOrdersFilters/index.vue'
import ExpressStatus from '@/modules/OrdersHistoryModule/components/ExpressStatus/index.vue'
import ExpressLink from '@/modules/OrdersHistoryModule/components/ExpressLink/index.vue'

// types
import { DeliveryGetOrdersFilters, DeliveryOrderT } from '@/core/types/delivery.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisation
import { delivery } from '@/core/realisations/delivery.realisation'
import useHelpers from '@/common/composable/useHelpers'

// constants
import { ExpressOrderColumns } from '@/core/constants/columns/ExpressOrder.columns'

// composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'express-orders-filters': ExpressOrdersFilters,
        ExpressStatus,
        TheButton,
        ExpressLink,
        TheTable,
        TableScrollWrapper,
    },
    setup() {
        const { findItemIndexById } = useHelpers()
        const state = reactive(new DataState<DeliveryOrderT>({}))
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const filters_state = reactive({
            limit: 50,
            //
            company_id: null as number | null,
            user_id: null as number | null,
            city_id: null as number | null,
            status_id: null as number | null,
            company_name: null as string | null,
            company_bin: null as string | null,
            createdAtFrom: null as string | null,
            createdAtTo: null as string | null,
            invoiceNumber: null as string | null,
            containerName: null as string | null,
            transportType: null as string | null,
            origin: null as number | null,
        })

        const loadExpressOrders = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const filters = {
                    page,
                    ...filters_state,
                }

                const response = await delivery.getExpressOrders(filters)
                state.list = response.data
                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total

                state.is_failed = false
                state.failure.error_state.is_cancel = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadExpressOrders(1)
        })

        const applyFilters = (filters: DeliveryGetOrdersFilters) => {
            filters_state.company_id = filters.company_id
            filters_state.user_id = filters.user_id
            filters_state.city_id = filters.city_id
            filters_state.status_id = filters.status_id
            filters_state.company_name = filters.company_name
            filters_state.company_bin = filters.company_bin
            filters_state.createdAtFrom = filters.createdAtFrom
            filters_state.createdAtTo = filters.createdAtTo
            filters_state.invoiceNumber = filters.invoiceNumber
            filters_state.containerName = filters.containerName
            filters_state.transportType = filters.transportType
            filters_state.origin = filters.origin

            loadExpressOrders(1)
        }

        const resetFilters = () => {
            filters_state.company_id = null
            filters_state.user_id = null
            filters_state.city_id = null
            filters_state.status_id = null
            filters_state.company_name = null
            filters_state.company_bin = null
            filters_state.createdAtFrom = null
            filters_state.createdAtTo = null
            filters_state.invoiceNumber = null
            filters_state.containerName = null
            filters_state.transportType = null
            filters_state.origin = null

            loadExpressOrders(1)
        }

        const onAddedToAccrual = (order_id: number) => {
            const index = findItemIndexById(order_id, state.list)
            if (index < 0) return

            state.list[index].is_added_to_accrual = 1
        }

        const cutName = (name: string): string => {
            let max_length = 12

            if (name && name.length > max_length) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, max_length - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const addToAccrual = async (item: DeliveryOrderT) => {
            const answer = confirm(
                `Вы действительно хотите добавить в начисления 1С заказ ${item.invoice_number} (ID: ${item.id}) ?`
            )
            if (!answer) return

            try {
                state.is_loading = true

                await delivery.addToAccrual(item.id)

                notifications.notify({ title: 'Заказ добавлен в начисления 1С', type: 'success' })
                onAddedToAccrual(item.id)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При добавлении заказа в начисление 1С произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const isCanAddToAccrual = (item: DeliveryOrderT) => {
            if (
                !item.is_billing_enabled &&
                !item.is_added_to_accrual &&
                havePermissions(['delivery-raketa-admin-order-accural', 'all-root-permission'])
            ) {
                return true
            }

            return false
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadExpressOrders(state.pagination.current_page)
        }

        return {
            state,
            filters_state,
            //
            loadExpressOrders,
            applyFilters,
            resetFilters,
            onAddedToAccrual,
            cutName,
            onLimitChange,
            //
            ExpressOrderColumns,
            //
            isCanAddToAccrual,
            addToAccrual,
        }
    },
})
